<!------------------------------------------------------------------------------------>
<!---------------------------------- 월별 정산 요약 ----------------------------------->
<!------------------------------------------------------------------------------------>
<template>
  <v-container fluid fill-height align-start pa-0 class="pageWrap monthly-history">
    <v-sheet width="100%" height="100%" class="monthly-history__sheet">
      <div class="monthly-history__grid">
        <div class="monthly-history__chart-col">
          <v-card class="stat-card stat-card--chart" elevation="0">
            <echart class="echartBar" ref="summaryChart" :options="customOptions" autoresize></echart>
          </v-card>
        </div>
        <div class="monthly-history__stats-col">
          <!----------- 매출 ----------->
          <v-card class="stat-card" elevation="0">
            <span class="stat-card__title">매출</span>
            <div class="stat-card__row">
              <span class="stat-card__data">
                <span class="stat-card__label">토스 매출</span>
                <span>{{ `${formatToKoreanWon(totalPgPaymentAmount)}원` }}</span>
              </span>
            </div>
            <div class="stat-card__row">
              <span class="stat-card__data">
                <span class="stat-card__label">계좌이체 매출</span>
                <span>{{ `${formatToKoreanWon(totalAcPaymentAmount)}원` }}</span>
              </span>
            </div>
            <div class="stat-card__row">
              <span class="stat-card__data stat-card__data--total">
                <span class="stat-card__label">총 매출</span>
                <span class="stat-card__value">{{ `${formatToKoreanWon(totalPaymentAmount)}원` }}</span>
              </span>
            </div>
          </v-card>

          <!----------- 지출 ----------->
          <v-card class="stat-card" elevation="0">
            <span class="stat-card__title">지출</span>
            <div class="stat-card__row">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span class="stat-card__data">
                    <span class="stat-card__label d-flex align-center">
                      환불
                      <v-icon small color="primary" v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                    </span>
                    <span>{{ `${formatToKoreanWon(totalCancelAmount)}원` }}</span>
                  </span>
                </template>
                <span>{{ '결제 승인 거절에 따른 금액 포함' }}</span>
              </v-tooltip>
            </div>
            <div class="stat-card__row">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span class="stat-card__data">
                    <span class="stat-card__label d-flex align-center">
                      토스 수수료
                      <v-icon small color="primary" v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                    </span>
                    <span>{{ `${formatToKoreanWon(totalTossCommissionAmount)}원` }}</span>
                  </span>
                </template>
                <span>{{ '결제 건 수에 대한 수수료' }}</span>
              </v-tooltip>
            </div>
            <div class="stat-card__row">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span class="stat-card__data stat-card__data--clickable" @click="goTaxInvoiceManage">
                    <span class="stat-card__label d-flex align-center">
                      세금 계산서 수수료
                      <v-icon small color="primary" v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                    </span>
                    <span>{{ `${formatToKoreanWon(totalUPlusCommissionAmount)}원` }}</span>
                  </span>
                </template>
                <span>{{ '주문에 대한 세금계산서 발행 수수료, 클릭 시 세금계산서 발행 페이지로 이동합니다.' }}</span>
              </v-tooltip>
            </div>
            <div class="stat-card__row">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span class="stat-card__data">
                    <span class="stat-card__label d-flex align-center">
                      유지비
                      <v-icon small color="primary" v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                    </span>
                    <span>{{ `${formatToKoreanWon(totalMaintenanceAmount)}원` }}</span>
                  </span>
                </template>
                <span>{{ 'PG사(토스) 연간 유지비' }}</span>
              </v-tooltip>
            </div>
            <div class="stat-card__row">
              <span class="stat-card__data stat-card__data--total">
                <span class="stat-card__label">총 지출</span>
                <span class="stat-card__value">{{ `${formatToKoreanWon(totalExpendAmount)}원` }}</span>
              </span>
            </div>
          </v-card>
          <!----------- 판매 이익 ----------->
          <v-card class="stat-card" elevation="0">
            <span class="stat-card__title">판매 이익</span>
            <div class="stat-card__row">
              <span class="stat-card__data">
                <span class="stat-card__label">총 컨설팅 수익</span>
                <span :style="getTextStyle(totalExtraAmount)" class="stat-card__value--dynamic">{{
                  `${formatToKoreanWon(totalExtraAmount)}원`
                }}</span>
              </span>
            </div>
            <div class="stat-card__row">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span class="stat-card__data">
                    <span class="stat-card__label d-flex align-center">
                      총 판매 이익
                      <v-icon small color="primary" v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                    </span>
                    <span :style="getTextStyle(totalProfitAmount)" class="stat-card__value--dynamic">{{
                      `${formatToKoreanWon(totalProfitAmount)}원`
                    }}</span>
                  </span>
                </template>
                <span>{{ '총 매출 - 총 지출' }}</span>
                <br />
                <span>{{ '(컨설팅 수익 별도)' }}</span>
              </v-tooltip>
            </div>
          </v-card>
        </div>
        <div class="monthly-history__split-col">
          <!----------- 이익 배분 ----------->
          <v-card class="stat-card stat-card--split" elevation="0">
            <div class="stat-card__split-inner">
              <span class="stat-card__split-label">일반</span>
              <v-divider class="stat-card__split-divider-h" />
              <div class="stat-card__split-body">
                <span class="stat-card__label">안전기술원</span>
                <span class="stat-card__data stat-card__data--compact">
                  <span />
                  <span class="stat-card__value--dynamic" :style="getTextStyle(totalProfitAmountKist)">{{
                    `${formatToKoreanWon(totalProfitAmountKist)}원`
                  }}</span>
                </span>
                <v-divider class="my-2" />
                <span class="stat-card__label">(주)일주지앤에스</span>
                <span class="stat-card__data stat-card__data--compact">
                  <span />
                  <span class="stat-card__value--dynamic" :style="getTextStyle(totalProfitAmountGns)">{{
                    `${formatToKoreanWon(totalProfitAmountGns)}원`
                  }}</span>
                </span>
              </div>
            </div>
          </v-card>
          <!----------- 이익 배분 (프로모션) ----------->
          <v-card class="stat-card stat-card--split" elevation="0">
            <div class="stat-card__split-inner">
              <span class="stat-card__split-label">프로모션</span>
              <v-divider class="stat-card__split-divider-h" />
              <div class="stat-card__split-body">
                <span class="stat-card__label">안전기술원 <span class="stat-card__ratio">(25%)</span></span>
                <span class="stat-card__data stat-card__data--compact">
                  <span />
                  <span class="stat-card__value--dynamic" :style="getTextStyle(totalProfitAmountProm)">{{
                    `${formatToKoreanWon(totalProfitAmountPromKist)}원`
                  }}</span>
                </span>
                <v-divider class="my-2" />
                <span class="stat-card__label">(주)일주지앤에스 <span class="stat-card__ratio">(75%)</span></span>
                <span class="stat-card__data stat-card__data--compact">
                  <span />
                  <span class="stat-card__value--dynamic" :style="getTextStyle(totalProfitAmountProm)">{{
                    `${formatToKoreanWon(totalProfitAmountPromGns)}원`
                  }}</span>
                </span>
              </div>
            </div>
          </v-card>
          <!----------- 합계 ----------->
          <v-card class="stat-card stat-card--split" elevation="0">
            <div class="stat-card__split-inner">
              <span class="stat-card__split-label">합계</span>
              <v-divider class="stat-card__split-divider-h" />
              <div class="stat-card__split-body">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span class="stat-card__label d-flex align-center">
                      안전기술원
                      <v-icon small color="primary" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                    </span>
                  </template>
                  <span>{{ '일반 + 프로모션' }}</span>
                  <br />
                  <span>{{ `${formatToKoreanWon(totalProfitAmountKist)}원 + ${formatToKoreanWon(totalProfitAmountPromKist)}원` }}</span>
                </v-tooltip>
                <span class="stat-card__data stat-card__data--compact">
                  <span />
                  <span class="stat-card__value--dynamic" :style="getTextStyle(totalProfitAmountKist + totalProfitAmountPromKist)">{{
                    `${formatToKoreanWon(totalProfitAmountKist + totalProfitAmountPromKist)}원`
                  }}</span>
                </span>
                <v-divider class="my-2" />
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span class="stat-card__label d-flex align-center">
                      (주)일주지앤에스
                      <v-icon small color="primary" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                    </span>
                  </template>
                  <span>{{ '일반 + 프로모션' }}</span>
                  <br />
                  <span>{{ `${formatToKoreanWon(totalProfitAmountGns)}원 + ${formatToKoreanWon(totalProfitAmountPromGns)}원` }}</span>
                </v-tooltip>
                <span class="stat-card__data stat-card__data--compact">
                  <span />
                  <span class="stat-card__value--dynamic" :style="getTextStyle(totalProfitAmountGns + totalProfitAmountPromGns)">{{
                    `${formatToKoreanWon(totalProfitAmountGns + totalProfitAmountPromGns)}원`
                  }}</span>
                </span>
              </div>
            </div>
          </v-card>
        </div>
      </div>
    </v-sheet>
  </v-container>
</template>
<script>
import BaseDataGrid from '@/views/base/BaseDataGrid.vue' //20220509 LYJ 데이타 그리드 사용시 베이스 추가
import baseview from '@/views/base/baseview.vue' //20220414 LYJ base page 추가

import { getMonthlySettlementSummary } from '@/api/feeManage/settlementHistory.js'
import { getFirstDayCurrentMonth, getLastDayCurrentMonth } from '@/utils/common.js'
import chartOpts from './data/settlementHistoryChartData.js'
import echart from '@/components/echart/ECharts.vue' // echart 컴포넌트
export default {
  mixins: [baseview, BaseDataGrid], //20220414 LYJ base page 추가
  name: 'monthlyHistory',
  components: {
    echart,
  },
  props: {
    searchStartDate: {
      type: String,
      default: () => {
        return ''
      },
    },
    searchEndDate: {
      type: String,
      default: () => {
        return ''
      },
    },
  },
  data() {
    return {
      defaultChartOptions: chartOpts(),
      customOptions: chartOpts(),

      totalPaymentAmount: 0, // 총 매출액
      totalPgPaymentAmount: 0, // 총 매출 (토스)
      totalAcPaymentAmount: 0, // 총 매출 (계좌이체)
      totalCancelAmount: 0, // 총 환불
      totalProfitAmount: 0, // 총 판매 이익
      totalProfitAmountGns: 0, // 총 판매 이익의 일주
      totalProfitAmountKist: 0, // 총 판매 이익의 안전기술원

      tossCommissionAmount: 0, // 토스 기본 사용료 (조회조건에 연결제 결제월이 포함될 경우 해당 값이 증가함)
      UPlusCommissionAmount: 0, // U+ 전자문서 건당 수수료
      UPlusCommissionAmountPerMonth: 0, // U+ 전자문서 월 유지비
      totalTossCommissionAmount: 0, // pg사 총 수수료
      totalUPlusCommissionAmount: 0, // 세금 계산서 총 수수료
      totalMaintenanceAmount: 0, // 총 유지비
      totalExtraAmount: 0, // 총 추가 수익 (ex.컨설팅비용)
      totalExpendAmount: 0, // 총 지출액 (ex.컨설팅비용)
      commissionList: [], // 수수료 목록

      // ************** 프로모션 관련 **************

      totalPaymentAmountProm: 0, // 총 매출액
      totalPgPaymentAmountProm: 0, // 총 매출 (토스)
      totalAcPaymentAmountProm: 0, // 총 매출 (계좌이체)
      totalCancelAmountProm: 0, // 총 환불
      totalProfitAmountProm: 0, // 총 판매 이익
      totalCommissionAmountProm: 0, // 총 수수료
      totalProfitAmountPromGns: 0, // 총 판매 이익의 일주
      totalProfitAmountPromKist: 0, // 총 판매 이익의 안전기술원
    }
  },
  computed: {},
  beforeMount() {},
  activated() {},
  mounted() {},
  methods: {
    customizeText(options) {
      // 출력되는 합계금액 커스텀
      return this.formatToKoreanWon(options.value) + '원'
    },
    customizeTotalText(options) {
      // 출력되는 합계금액 커스텀
      return '합계: ' + this.formatToKoreanWon(this.totalAmount) + '원'
    },
    customSummary(options) {
      // 2025.01.10 LJH 음수도 계산하도록 수정
      // 토스 결제에서 음수 제외하고 합산 구하는 함수
      const dataSource = options.component.option('dataSource') // 데이터 소스에서 데이터를 가져오기
      const field = options.name
      let sum = 0
      if (dataSource.length > 0) {
        // summaryProcess가 finalize일 때 합산을 마무리
        if (options.summaryProcess === 'finalize') {
          dataSource.forEach((item) => {
            sum += parseInt(item[field] != null ? item[field] : 0)
          })
          // summary 타입의 값을 설정
          options.totalValue = sum
          if (field === 'payOutAmount') {
            this.totalAmount += sum
          }
        }
      }
    },
    // 금액에 따른 텍스트 스타일 return
    getTextStyle(amount) {
      let style = 'color:#B4B4B4;'
      if (amount === 0) {
        style = `color:#B4B4B4;`
      } else if (amount < 0) {
        style = `color:#FF2D2D;`
      } else if (amount > 0) {
        style = `color:#1B53F3;`
      }
      // 금액 길이에 따라 폰트사이즈 조절
      const length = String(amount).length
      let fontSize = 'font-size:1.1rem;'
      if (length > 15) {
        fontSize = 'font-size:0.6rem;'
      } else if (length > 12) {
        fontSize = 'font-size:0.7rem;'
      } else if (length > 9) {
        fontSize = 'font-size:1.0rem;'
      }
      style = `${style} ${fontSize}`
      return style
    },
    async btnSearch(param, isNotify = true) {
      // 월별 정산 요약
      // 매출
      this.totalPgPaymentAmount = 0
      this.totalAcPaymentAmount = 0
      this.totalPaymentAmount = 0

      // 지출
      this.totalCancelAmount = 0
      this.totalTossCommissionAmount = 0
      this.totalUPlusCommissionAmount = 0
      this.totalMaintenanceAmount = 0
      this.totalExpendAmount = 0

      // 판매이익
      this.totalExtraAmount = 0
      this.totalProfitAmount = 0

      // 수익분배
      this.totalProfitAmountKist = 0
      this.totalProfitAmountGns = 0
      this.totalProfitAmountPromKist = 0
      this.totalProfitAmountPromGns = 0
      this.totalProfitAmountProm = 0

      this.customOptions = _.cloneDeep(this.defaultChartOptions)
      this.openLoading()
      getMonthlySettlementSummary(param, false)
        .then((res) => {
          res.list.forEach((el) => {
            this.customOptions.xAxis.data.push(el.orderMonth)
            this.customOptions.series[0].data.push(el.pgPaymentAmount) // 토스 결제 매출
            this.customOptions.series[1].data.push(el.acPaymentAmount) // 직접계좌이체 매출
            this.customOptions.series[2].data.push(el.cancelAmount) // 환불금액
            this.customOptions.series[3].data.push(el.maintenanceAmount) // 유지비
            this.customOptions.series[4].data.push(el.tossCommissionAmount + el.uplusCommissionAmount) // 수수료
            this.customOptions.series[5].data.push(el.profit) // 판매이익

            // -------- 매출 --------
            this.totalPgPaymentAmount += el.pgPaymentAmount // 토스 매출
            this.totalAcPaymentAmount += el.acPaymentAmount // 계좌이체 매출
            // -------- 지출 --------
            this.totalCancelAmount += el.cancelAmount // 환불
            this.totalTossCommissionAmount += el.tossCommissionAmount // 토스 수수료
            this.totalUPlusCommissionAmount += el.uplusCommissionAmount // 세금계산서 수수료
            this.totalMaintenanceAmount += el.maintenanceAmount // 유지비
            // -------- 판매이익 --------
            this.totalExtraAmount += el.extraAmount // 총 컨설팅 수익

            // -------- 프로모션 --------
            this.totalProfitAmountProm += el.promotionAmount - el.promotionCancel - el.promotionPgFee - el.promotionTaxFee

            const monthPaymentAmount = el.pgPaymentAmount + el.acPaymentAmount // 해당 월의 매출
            const monthExpendAmount = el.cancelAmount + el.tossCommissionAmount + el.uplusCommissionAmount + el.maintenanceAmount // 해당 월의 지지출
            const monthProfitAmount = monthPaymentAmount - monthExpendAmount - el.extraAmount // 해당 월의 판매이익
            const monthPromProfitAmount = el.promotionAmount - el.promotionCancel - el.promotionPgFee - el.promotionTaxFee // 해당 월의 프로모션 판매이익

            // -------- 수익 분배 --------
            // 일반
            const monthDivided = (monthProfitAmount - monthPromProfitAmount) / 2 // 해당월의 일반 요금 이익배분
            this.totalProfitAmountKist = this.totalProfitAmountKist + (Math.floor(monthDivided * 1) + el.extraAmount)
            this.totalProfitAmountGns = this.totalProfitAmountGns + Math.round(monthDivided * 1)

            // 프로모션
            const monthDividedProm = monthPromProfitAmount / 4
            this.totalProfitAmountPromKist = this.totalProfitAmountPromKist + Math.round(monthDividedProm * 1) // 반올림
            this.totalProfitAmountPromGns = this.totalProfitAmountPromGns + Math.floor(monthDividedProm * 3) // 반내림
          })

          this.totalPaymentAmount = this.totalPgPaymentAmount + this.totalAcPaymentAmount // 총 매출
          this.totalExpendAmount =
            this.totalCancelAmount + this.totalTossCommissionAmount + this.totalUPlusCommissionAmount + this.totalMaintenanceAmount // 총 지출
          this.totalProfitAmount = this.totalPaymentAmount - this.totalExpendAmount - this.totalExtraAmount // 총 판매이익
        })
        .catch(() => {
          this.endLoading()
        })
        .finally(() => {
          this.endLoading()
        })
    },
    ///////////////////////////////////////
    /*             etc Event             */
    ///////////////////////////////////////
    // 세금계산서 발행 페이지 이동
    goTaxInvoiceManage() {
      const toRouter = {
        name: 'TaxInvoiceManage',
        params: {
          data: { startDate: getFirstDayCurrentMonth(this.searchStartDate), endDate: getLastDayCurrentMonth(this.searchEndDate), flag: true },
        },
      }
      this.$router.push(toRouter)
    },
    formatToKoreanWon(value) {
      if (value) {
        const numberValue = parseFloat(value.toString().replace(/,/g, ''))
        if (!isNaN(numberValue)) {
          return numberValue.toLocaleString('ko-KR')
        }
      } else return 0
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@/assets/theme/variables.scss';

// 레이아웃 간격 (하드코딩 제거, 변수로 관리)
$card-gap: 12px;
$card-padding: 16px 18px;
$card-radius: 10px;
$row-gap: 6px;
$chart-min-height: 320px;

.monthly-history {
  &__sheet {
    min-height: 100%;
  }

  &__grid {
    display: flex;
    gap: $card-gap;
    height: 100%;
    min-height: 0;
    align-items: stretch;
  }

  &__chart-col {
    flex: 5 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__stats-col {
    flex: 3 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: $card-gap;
    overflow-y: auto;
  }

  &__split-col {
    flex: 2 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

.stat-card {
  border-radius: $card-radius;
  background: #f3f6fa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: $card-padding;
  flex-shrink: 0;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  &--chart {
    flex: 1;
    min-height: $chart-min-height;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &--split {
    flex-shrink: 0;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: $main_text;
    display: block;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 1px solid $divider;
  }

  &__row {
    margin-top: $row-gap;

    &:first-of-type {
      margin-top: 4px;
    }
  }

  &__data {
    width: 100%;
    padding: 8px 10px;
    background: $white;
    border-radius: 6px;
    text-align: right;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: $sub_text;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: normal;
    white-space: nowrap;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

    &--total .stat-card__value,
    &--total {
      font-size: 1.15rem;
      font-weight: 700;
      color: $main_text;
    }

    &--clickable {
      cursor: pointer;

      &:hover {
        background: #f8f9fc;
      }
    }

    &--compact {
      padding: 6px 10px;
      margin-top: 2px;
    }
  }

  &__label {
    font-size: 0.9rem;
    font-weight: 500;
    color: $main_text;
  }

  &__value {
    font-weight: 700;
    color: $main_text;
  }

  &__value--dynamic {
    font-weight: 700;
  }

  &__ratio {
    font-size: 0.75rem;
    color: $sub_title;
    font-weight: 400;
  }

  &__split-inner {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    min-height: 0;
  }

  &__split-label {
    font-size: 0.95rem;
    font-weight: 600;
    color: $main_text;
    flex-shrink: 0;
    padding-bottom: 8px;
    display: block;
  }

  &__split-divider-h {
    flex-shrink: 0;
    margin-bottom: 8px;
  }

  &__split-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
}

.echartBar {
  flex: 1;
  min-height: 280px;
  padding: 8px;
}
</style>
